import { useNavigate } from 'react-router-dom';
import { useEngine } from '../api/context';
import { useTheme } from '../design/theme';
import { Avatar } from '../design/primitives/Misc';
import { DropdownMenu, type MenuItemDef, type Placement } from '../design/primitives/Overlay';
import { Icon, type IconName } from '../design/icons';
import { useShell } from './ShellProvider';
import { openExternal, systemName, useDataSource } from '../meta/useDataSource';
import { requestStepUp } from '../api/stepUp';

// The account menu, shared by every skin so the skin switcher, theme choices and sign-out
// are identical everywhere. `useSkinMenuItems()` is also exported for skins (Desktop's View
// menu) that want the skin choices without the whole account button.

const THEME_ITEMS = (theme: ReturnType<typeof useTheme>): MenuItemDef[] => [
  { label: 'System', icon: 'monitor', checked: theme.pref === 'system', onSelect: () => theme.setPref('system') },
  { label: 'Light', icon: 'sun', checked: theme.pref === 'light', onSelect: () => theme.setPref('light') },
  { label: 'Dark', icon: 'moon', checked: theme.pref === 'dark', onSelect: () => theme.setPref('dark') },
];

/** Menu items for the skin switcher: a heading then one checkable item per registered skin. */
export function useSkinMenuItems(): MenuItemDef[] {
  const { skin, setSkin, skins, isPhone } = useShell();
  // On a phone the shell forces the one skin that fits (ShellProvider.PHONE_SKIN), so offering
  // the switcher would be offering a choice that does nothing.
  if (isPhone || skins.length <= 1) return [];
  return [
    { heading: true, label: 'Workspace style' },
    ...skins.map((s) => ({ key: `skin-${s.id}`, label: s.label, hint: s.description, icon: s.icon, checked: s.id === skin, onSelect: () => setSkin(s.id) })),
  ];
}

/** The account button + menu. */
export function UserMenu({ placement = 'top-start', onShortcuts }: { placement?: Placement; onShortcuts?: () => void }) {
  const { principal, signOut } = useEngine();
  const theme = useTheme();
  const navigate = useNavigate();
  const skinItems = useSkinMenuItems();
  // A tenant running on its connected system (Halo) can jump to that system's own UI — its home and
  // its configuration, which NovaPSA links to rather than re-implements.
  const { overlay } = useDataSource();
  const app = overlay?.app;
  const systemItems: MenuItemDef[] = overlay && app
    ? [
        { separator: true, label: '' },
        { label: `Open ${systemName(overlay)}`, icon: 'external-link', onSelect: () => openExternal(app.url) },
        { label: `${systemName(overlay)} configuration`, icon: 'settings', onSelect: () => openExternal(app.config_url) },
      ]
    : [];
  const items: MenuItemDef[] = [
    { heading: true, label: principal.email ?? 'Account' },
    // Platform operators can switch to the cross-tenant admin console (its own shell +
    // /v1/admin engine). Shown only when the token carries a platform role; the backend
    // still enforces access via requirePlatformAdmin.
    ...(principal.platformRole ? [{ label: 'Platform admin', icon: 'shield-check' as IconName, onSelect: () => navigate('/admin') }] : []),
    ...(onShortcuts ? [{ label: 'Keyboard shortcuts', icon: 'keyboard' as IconName, hint: '?', onSelect: onShortcuts }] : []),
    { label: 'Connected apps', icon: 'plug-zap' as IconName, onSelect: () => navigate('/connected-apps') },
    { label: 'Two-factor authentication', icon: 'lock' as IconName, onSelect: () => requestStepUp({ mode: 'manage' }) },
    ...systemItems,
    { heading: true, label: 'Theme' },
    ...THEME_ITEMS(theme),
    ...(skinItems.length ? [{ separator: true, label: '' }, ...skinItems] : []),
    { separator: true, label: '' },
    { label: 'Sign out', icon: 'log-out', danger: true, onSelect: () => void signOut?.() },
  ];
  return (
    <DropdownMenu
      aria-label="Account"
      placement={placement}
      trigger={
        <button type="button" className="hf-user" aria-label="Account menu">
          <Avatar name={principal.name ?? principal.email ?? '?'} size="sm" />
          <span className="lbl">
            <span className="n">{principal.name ?? principal.email ?? 'Signed in'}</span>
            {principal.name && principal.email && <span className="e">{principal.email}</span>}
          </span>
          <Icon name="chevrons-up-down" size={13} className="hf-muted" />
        </button>
      }
      items={items}
    />
  );
}
